import React, { useEffect, useState } from "react";
import styled from "styled-components";
import Slider from "react-slick";
import Image from "../../components/parts/Image";
import Tag from "../../components/parts/Tag";
import Text from "../../components/parts/Text";
import Divider from "../../components/parts/Divider";
import HeaderOne from "../../components/parts/HeaderOne";
import Table from "../../components/parts/Table";
import HeaderTwo from "../../components/parts/HeaderTwo";
import {
  GetAdministratorShop200Response,
  GetAdministratorDraftShop200Response,
  PostAdministratorDraftShopApprovalRequestApprovalStatusEnum,
  GetAdministratorDraftShop200ResponseApprovalStatusEnum,
  GetOwnerDraftShop200ResponseApprovalStatusEnum,
} from "../../../api/v1";
import { api } from "../../../api";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import parse from "html-react-parser";
import AdministratorLayout from "../../components/layouts/AdministratorLayout";
import Button from "../../components/parts/Button";
import { useNavigate } from "react-router";
import Alert from "../../components/parts/Alert";
import * as Sentry from "@sentry/react";
import Annotation from "../../components/parts/Annotation";

const HeaderOneStyle = styled(HeaderOne)`
  margin-bottom: 20px;
  text-align: center;

  span {
    white-space: nowrap;
  }
`;

const DraftShopStyle = styled.div`
  background: white;
  border-radius: 25px;
  padding: 20px 30px;
  margin: 30px;

  @media (max-width: 767px) {
    padding: 20px 10px;
    margin: 30px 10px;
  }
`;

const DraftShopImagesStyle = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 40px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    img {
      margin-right: 0;
      width: calc(50% - 5px);
    }
  }
`;

const SlickDraftShopImagesStyle = styled.div`
  margin-bottom: 40px;
`;

const DraftShopCardsSliderStyle = styled.div`
  .slick-list {
    margin-right: -9px;
    padding-bottom: 20px;
  }

  .slick-slide > div {
    padding: 0 10px 10px 0;
  }
`;

const GoogleMapStyle = styled.div`
  margin-bottom: 30px;
  iframe {
    width: 100%;
    height: 300px;
  }
`;

const ButtonsGroupStyle = styled.div`
  text-align: center;
  margin-top: 20px;
`;

const ButtonsStyle = styled.div`
  display: flex;
  flex-flow: column;
  align-items: center;
  gap: 10px;
`;

const ButtonStyle = styled(Button)`
  max-width: 250px;
  width: 100%;
`;

const RejectButtonStyle = styled(Button)`
  background: #f2f0e9;
  color: #393432;
  max-width: 250px;
  width: 100%;

  &:hover {
    background: #e0ded7;
  }
`;

const DraftShop: React.FC = () => {
  const settings = {
    adaptiveHeight: true,
    arrows: false,
    autoplay: false,
    dots: true,
    infinite: true,
    slidesToShow: 4,
    slidesToScroll: 1,
    responsive: [
      {
        breakpoint: 1000,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
          rows: 1,
        },
      },
    ],
  };
  const dayOfWeeks = {
    sunday: "日曜日",
    monday: "月曜日",
    tuesday: "火曜日",
    wednesday: "水曜日",
    thursday: "木曜日",
    friday: "金曜日",
    saturday: "土曜日",
    holiday: "祝日",
  };
  const timeframe = {
    morning: "モーニング",
    lunch: "ランチ",
    dinner: "ディナー",
  };
  const [shop, setShop] = useState<GetAdministratorShop200Response>();
  const [draftShop, setDraftShop] =
    useState<GetAdministratorDraftShop200Response>();
  const [message, setMessage] = useState(null);
  const [messageLevel, setMessageLevel] = useState(null);
  const [approvalSubmitting, setApprovalSubmitting] = useState(false);
  const [rejectSubmitting, setRejectSubmitting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const id = location.pathname.split("/").pop();

    api
      .getAdministratorDraftShop(Number(id))
      .then((response) => {
        setDraftShop(response.data);
      })
      .catch((error) => {
        if (error.response?.status === 401)
          location.href = "/administrator/login";
        Sentry.captureException(error);
      });
  }, []);

  useEffect(() => {
    if (draftShop) {
      api
        .getAdministratorShop(Number(draftShop.shopId))
        .then((response) => {
          setShop(response.data);
        })
        .catch((error) => {
          if (error.response?.status === 401) location.href = "/shops";
          Sentry.captureException(error);
        });
    }
  }, [draftShop]);

  const onClickSaveButton = async (
    approvalStatus: PostAdministratorDraftShopApprovalRequestApprovalStatusEnum
  ) => {
    if (approvalStatus === "approved") {
      setApprovalSubmitting(true);
    }
    if (approvalStatus === "rejected") {
      setRejectSubmitting(true);
    }
    await api
      .postAdministratorDraftShopApproval({
        draftShopId: draftShop.id,
        approvalStatus: approvalStatus,
      })
      .then((response) => {
        if (response.data.approval_status === "approved") {
          navigate(`/administrator/shops/${response.data.shop_id}`, {
            state: {
              message: response.data.message,
              level: "success",
            },
          });
        } else {
          navigate(`/administrator/draft-shops/${draftShop.id}`, {
            state: {
              message: response.data.message,
              level: "success",
            },
          });
          window.location.reload();
        }
      })
      .catch((error) => {
        setMessage(error.response.data.message);
        setMessageLevel("error");
      });
    setApprovalSubmitting(false);
    setRejectSubmitting(false);
  };

  if (!draftShop) return;

  const PageTitle = ({
    approvalStatus,
  }: {
    approvalStatus: GetAdministratorDraftShop200ResponseApprovalStatusEnum;
  }) => {
    switch (approvalStatus) {
      case GetAdministratorDraftShop200ResponseApprovalStatusEnum.Approved:
        return <HeaderOneStyle>申請の確認（承認済み）</HeaderOneStyle>;
      case GetAdministratorDraftShop200ResponseApprovalStatusEnum.Rejected:
        return <HeaderOneStyle>申請の確認（却下）</HeaderOneStyle>;
      default:
        return <HeaderOneStyle>申請の確認（未承認）</HeaderOneStyle>;
    }
  };

  return (
    <AdministratorLayout>
      <DraftShopStyle>
        <PageTitle approvalStatus={draftShop.approvalStatus} />
        {draftShop.owner && (
          <Text size="12px">
            <b>⭐️オーナーからの店舗追加申請⭐️</b>
          </Text>
        )}
        <Text size="12px" mb="5px">
          申請日時：{new Date(draftShop.createdAt).toLocaleString()}
        </Text>
        <Annotation level="default" mb="10px">
          <Text size="14px" weight="bold">
            変更内容
          </Text>
          <Text size="12px" weight="bold" mb="5px">
            画像は目視チェックしてください。
          </Text>
          <Text size="12px" weight="bold" mb="5px">
            営業時間は目視チェックしてください。
          </Text>
          {shop &&
            draftShop &&
            Object.keys(draftShop).map((key) => {
              // FIXME: ロジックが多いのでリファクタリングしたい
              // NOTE: 比較しなくよい項目はスキップ
              if (
                [
                  "id",
                  "createdAt",
                  "updatedAt",
                  "userId",
                  "shopId",
                  "approvalStatus",
                  "approvalFlag",
                  "user",
                  "images",
                  "draftOpeningHours",
                ].includes(key)
              )
                return null;

              let shopKey = key;
              if (shopKey === "draftBudgets") shopKey = "budgets";

              // 予算はモーニング、ランチ、ディナーの3つがあるので、それぞれ比較する
              if (key === "draftBudgets") {
                return draftShop.draftBudgets.map((draftBudget, index) => {
                  const shopBudget = shop.budgets.find(
                    (budget) => budget.timeframe === draftBudget.timeframe
                  );
                  if (
                    shopBudget.lower !== draftBudget.lower ||
                    shopBudget.upper !== draftBudget.upper
                  ) {
                    return (
                      <Text size="12px" key={index}>
                        {key}の{shopBudget.timeframe}が変更されました
                      </Text>
                    );
                  }
                  return null;
                });
              }

              // オブジェクト型も比較するためにJSON.stringifyで変換して比較できるようにする
              const shopValue = JSON.stringify(shop[shopKey]);
              const draftShopValue = JSON.stringify(draftShop[key]);
              if (shopValue !== draftShopValue)
                return <Text size="12px">{key}が変更されました</Text>;

              if (!shop[shopKey] && draftShop[key])
                return <Text size="12px">{key}が新たに追加されました</Text>;
              return null;
            })}
        </Annotation>
        <Text size="12px" mb="2px">
          {/* オーナーを優先表示 */}
          {draftShop.owner ? (
            <b>申請者：{draftShop.owner.name}（オーナー）</b>
          ) : draftShop.user ? (
            <b>申請者：{draftShop.user.username}（一般ユーザー）</b>
          ) : null}
        </Text>
        <HeaderOne>{draftShop.name}</HeaderOne>
        <Text size="12px">{draftShop.nameKana}</Text>
        {draftShop.owner && <Tag mr="5px">公式</Tag>}
        {draftShop.nearestStation && (
          <Tag mr="5px">{draftShop.nearestStation}</Tag>
        )}
        {draftShop.features
          .map((feature) => feature.name)
          .includes("モーニング") && <Tag mr="5px">モーニング</Tag>}
        {draftShop.features
          .map((feature) => feature.name)
          .includes("ランチ") && <Tag mr="5px">ランチ</Tag>}
        {draftShop.features
          .map((feature) => feature.name)
          .includes("ディナー") && <Tag>ディナー</Tag>}
        <Divider mt="20px" mb="20px" />
        {draftShop.images.length <= 4 ? (
          <DraftShopImagesStyle>
            {draftShop.images.map((image, index) => {
              return <Image key={index} src={image.url} alt="店舗画像" />;
            })}
          </DraftShopImagesStyle>
        ) : (
          <SlickDraftShopImagesStyle>
            <DraftShopCardsSliderStyle>
              <Slider {...settings}>
                {draftShop.images.map((image, index) => (
                  <Image
                    key={index}
                    src={image.url}
                    width="100%"
                    alt="店舗画像"
                  />
                ))}
              </Slider>
            </DraftShopCardsSliderStyle>
          </SlickDraftShopImagesStyle>
        )}
        <Text size="16px" mb="30px">
          {draftShop.description}
        </Text>
        {draftShop.googleMapIframe && (
          <section>
            <HeaderTwo mb="8px">Googleマップ</HeaderTwo>
            <GoogleMapStyle>{parse(draftShop.googleMapIframe)}</GoogleMapStyle>
          </section>
        )}
        <section>
          <HeaderTwo mb="5px">店舗基本情報</HeaderTwo>
          <Table
            contents={[
              {
                head: "営業時間",
                data: draftShop.draftOpeningHours.map((openingHour, index) => {
                  return (
                    <>
                      <p key={index}>
                        {`${dayOfWeeks[openingHour.dayOfWeek]} ${
                          openingHour.openingTime
                        } 〜 ${openingHour.closingTime}`}
                      </p>
                      {draftShop.draftOpeningHours.length === index + 1 &&
                        "営業時間や定休日は変更になっている場合がございますので、ご来店前に店舗にご確認ください。"}
                    </>
                  );
                }),
              },
              { head: "電話番号", data: draftShop.tel },
              { head: "所在地", data: draftShop.address },
              { head: "最寄駅", data: draftShop.nearestStation },
              {
                head: "お支払い方法",
                data: draftShop.paymentMethods
                  .map((paymentMethod) => paymentMethod.name)
                  .join("、"),
              },
              {
                head: "予算",
                data: draftShop.draftBudgets.map((draftBudget) => {
                  return (
                    <p key={draftBudget.timeframe}>
                      {timeframe[draftBudget.timeframe]}：{draftBudget.lower}円
                      〜 {draftBudget.upper}円
                    </p>
                  );
                }),
              },
              {
                head: "座席数",
                data: draftShop.numberOfSeats && draftShop.numberOfSeats + "席",
              },
              {
                head: "ホームページ",
                data: (
                  <a href={draftShop.homepage} target="_blank">
                    {draftShop.homepage}
                  </a>
                ),
              },
              {
                head: "Googleマップ共有リンク",
                data: (
                  <a href={draftShop.googleMapUrl} target="_blank">
                    {draftShop.googleMapUrl}
                  </a>
                ),
              },
              {
                head: "特徴",
                data: draftShop.features
                  .map((feature) => feature.name)
                  .join("、"),
              },
              {
                head: "備考",
                data:
                  draftShop.note &&
                  parse(draftShop.note.replace(/\n/g, "<br />")),
              },
            ]}
          />
        </section>
        <ButtonsGroupStyle>
          {draftShop.approvalStatus ===
            GetOwnerDraftShop200ResponseApprovalStatusEnum.Unapproved && (
            <ButtonsStyle>
              <ButtonStyle
                onClick={() => onClickSaveButton("approved")}
                submitting={approvalSubmitting}
                submittingMessage={"承認中..."}
              >
                承認する
              </ButtonStyle>
              <RejectButtonStyle
                onClick={() => onClickSaveButton("rejected")}
                submitting={rejectSubmitting}
                submittingMessage={"却下中..."}
              >
                却下する
              </RejectButtonStyle>
            </ButtonsStyle>
          )}
          {draftShop.approvalStatus === "approved" && (
            <Text size="12px">この店舗は承認済みです。</Text>
          )}
          {message && <Alert level={messageLevel}>{message}</Alert>}
        </ButtonsGroupStyle>
      </DraftShopStyle>
    </AdministratorLayout>
  );
};
export default DraftShop;
